import { useFormik } from "formik";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Link, useHistory, withRouter } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { object, string } from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useCities,
  useCountries,
  useCreateArrondissement,
  useProvinces,
} from "../../../hooks/useGeography";

const AddArrondissement = (props) => {
  const history = useHistory();
  const createArrondissementMutation = useCreateArrondissement();

  document.title = props.t("Ajout d'un arrondissement - Le Conseiller du BTP");

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: "",
      countryLabel: "",
      pays: "",
      province: "",
      provinceLabel: "",
      city: "",
      cityLabel: "",
    },

    validationSchema: object().shape({
      name: string().required(
        props.t("Veuillez entrer le nom de l'arrondissement")
      ),
      pays: string().required(props.t("Veuillez sélectionner un pays")),
      province: string().required(
        props.t("Veuillez sélectionner une province")
      ),
      city: string().required(props.t("Veuillez sélectionner un département")),
    }),

    onSubmit: async (values) => {
      createArrondissementMutation.mutate(
        {
          name: values.name,
          cityId: values.city,
        },
        {
          onSuccess: () => {
            history.push("/geolocalisation-arrondissement");
          },
        }
      );
    },
  });

  const countriesQ = useCountries();
  const provincesQ = useProvinces(validation.values.pays);
  const citiesQ = useCities(validation.values.province);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t("Ajout d'un arrondissement")}
          pageTitle={props.t("Arrondissement")}
        />

        <Row>
          <Col lg={12}>
            <Form
              encType="multipart/form-data"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <Card>
                <CardBody>
                  {/* Sélection du pays */}
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="pays">
                      {props.t("Pays")}
                    </Label>
                    <Input
                      type="select"
                      className="form"
                      name="pays"
                      value={validation.values.pays}
                      onChange={(e) => {
                        const current = (countriesQ.data || []).find(
                          (el) => String(el.id) === e.target.value
                        );
                        validation.setFieldValue("pays", current?.id || "");
                        validation.setFieldValue("countryLabel", current?.name || "");
                        validation.setFieldValue("province", "");
                        validation.setFieldValue("provinceLabel", "");
                        validation.setFieldValue("city", "");
                        validation.setFieldValue("cityLabel", "");
                      }}
                      onBlur={validation.handleBlur}
                    >
                      <option value="">
                        {props.t("Sélectionner un pays")}
                      </option>
                      {(countriesQ.data || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Input>
                    {validation.errors.pays && validation.touched.pays && (
                      <FormFeedback type="invalid">
                        {validation.errors.pays}
                      </FormFeedback>
                    )}
                  </div>

                  {/* Sélection de la province */}
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="province">
                      {props.t("Province")}
                    </Label>
                    <Input
                      type="select"
                      className="form"
                      name="province"
                      value={validation.values.province}
                      disabled={!validation.values.pays || provincesQ.isLoading}
                      onChange={(e) => {
                        const current = (provincesQ.data || []).find(
                          (el) => String(el.id) === e.target.value
                        );
                        validation.setFieldValue("province", current?.id || "");
                        validation.setFieldValue("provinceLabel", current?.name || "");
                        validation.setFieldValue("city", "");
                        validation.setFieldValue("cityLabel", "");
                      }}
                      onBlur={validation.handleBlur}
                    >
                      <option value="">
                        {props.t("Sélectionner une province")}
                      </option>
                      {(provincesQ.data || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Input>
                    {validation.errors.province &&
                      validation.touched.province && (
                        <FormFeedback type="invalid">
                          {validation.errors.province}
                        </FormFeedback>
                      )}
                  </div>

                  <div className="mb-3">
                    <Label className="form-label" htmlFor="city">
                      {props.t("Ville")}
                    </Label>
                    <Input
                      type="select"
                      className="form"
                      name="city"
                      value={validation.values.city}
                      disabled={!validation.values.province || citiesQ.isLoading}
                      onBlur={validation.handleBlur}
                      onChange={(e) => {
                        const current = (citiesQ.data || []).find(
                          (el) => String(el.id) === e.target.value
                        );
                        validation.setFieldValue("city", current?.id || "");
                        validation.setFieldValue("cityLabel", current?.name || "");
                      }}
                      required
                    >
                      <option value="">Sélectionner la ville concernée</option>
                      {(citiesQ.data || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Input>
                    {validation.errors.city && validation.touched.city ? (
                      <FormFeedback type="invalid">
                        {validation.errors.city}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="name">
                      {props.t("Nom de l'arrondissement")}
                    </Label>
                    <Input
                      type="text"
                      className="form"
                      placeholder={props.t("Entrer le nom de l'arrondissement")}
                      name="name"
                      value={validation.values.name}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      required
                      disabled={
                        !validation.values.pays ||
                        !validation.values.province ||
                        !validation.values.city
                      }
                    />
                    {validation.errors.name && validation.touched.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
              <div className="text-end mb-3">
                <Link to="/geolocalisation-arrondissement" className="btn btn-primary me-2">
                  {props.t("Retour")}
                </Link>
                <button type="submit" className="btn btn-warning w-sm m-2">
                  {createArrondissementMutation.isPending
                    ? props.t("Chargement")
                    : props.t("Ajouter")}
                </button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

AddArrondissement.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(AddArrondissement));
